import { CollectionViewer } from '@angular/cdk/collections';
import {
  AgrDataSource,
  ExportableDataSource,
  PaginatedDataSource,
  PaginationModel,
  SortableDataSource,
  Sorting,
} from '@next/core-lib/table-v2';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { toRequestExportModel } from 'src/app/request/models/manure-request.model';
import { RequestService } from 'src/app/shared/services/request.service';

import { requestMakeMatchSuccess } from '../../overview-slide-in/requests/store/requests-overview-slide.actions';
import { OrderOptions, RequestsOverviewModel } from '../models/overview.model';
import { PageAndSize } from '../models/paginated-overview.model';
import * as OverviewActions from '../store/actions/overview.actions';
import * as OverviewSelectors from '../store/selectors/overview.selector';

/* eslint-disable brace-style */
export class RequestsOverviewDataSource
  extends AgrDataSource<RequestsOverviewModel>
  implements
    PaginatedDataSource<RequestsOverviewModel>,
    SortableDataSource<RequestsOverviewModel>,
    ExportableDataSource<RequestsOverviewModel>
{
  public data$ = new BehaviorSubject<RequestsOverviewModel[]>([]);
  requests$ = this.store.select(OverviewSelectors.selectRequestsList);
  totalNumberOfRequests$ = this.store.select(OverviewSelectors.getTotalNumberOfRequests);
  readonly _sortingSubject$ = new BehaviorSubject<Sorting>({});

  readonly order$: Observable<OrderOptions> = this._sortingSubject$.pipe(
    map((sorting) => {
      const sortColumn = Object.keys(sorting)[0];
      const sortDirection = sorting[sortColumn];

      return sortColumn && sortDirection
        ? { column: sortColumn, descending: sortDirection === 'desc' }
        : undefined;
    }),
  );

  private pageAndSizeSubject$ = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 25,
  });
  private readonly destroyPaginatorSubject$ = new Subject<void>();
  private readonly destroySubject$ = new Subject<boolean>();

  constructor(
    private store: Store,
    private actions$: Actions,
    private requestService: RequestService,
  ) {
    super();

    this.store
      .select(OverviewSelectors.selectOverviewRequestsLoading)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((loading) => {
        this.loadingSubject.next(loading);
      });

    combineLatest([this.pageAndSizeSubject$])
      .pipe(debounceTime(100), takeUntil(this.destroySubject$))
      .subscribe(([paginationOptions]) => {
        this.store.dispatch(OverviewActions.loadRequests({ paginationOptions }));
      });

    this.actions$
      .pipe(
        ofType(requestMakeMatchSuccess),
        withLatestFrom(this.pageAndSizeSubject$),
        takeUntil(this.destroySubject$),
        tap(([_, pageAndSize]) => {
          this.pageAndSizeSubject$.next(pageAndSize);
        }),
      )
      .subscribe();
  }

  prepareExportData<RequestExportModel>() {
    return this.requestService
      .getRequestsForExport()
      .pipe(
        map((response) => response.requests.map((request) => toRequestExportModel(request))),
      ) as Observable<RequestExportModel[]>;
  }

  connect(_: CollectionViewer): Observable<RequestsOverviewModel[]> {
    combineLatest([this.requests$, this.order$])
      .pipe(debounceTime(100), takeUntil(this.destroySubject$), shareReplay(1))
      .subscribe(([requests, order]) => {
        let sortedRequests = requests;

        if (order && order.column) {
          sortedRequests = this.sortRequests(requests, order.column, order.descending);
        }

        this.data$.next(sortedRequests);
      });
    return this.data$;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.destroySubject$.next(true);
    this.destroySubject$.complete();
  }

  connectPaginator(): Observable<PaginationModel> {
    return combineLatest([this.pageAndSizeSubject$, this.totalNumberOfRequests$]).pipe(
      takeUntil(this.destroyPaginatorSubject$),
      map(([{ pageIndex, pageSize }, totalNumberOfRequests]) => ({
        pageSize,
        pageIndex,
        length: totalNumberOfRequests,
      })),
    );
  }

  disconnectPaginator(): void {
    if (!this.destroyPaginatorSubject$.isStopped) {
      this.destroyPaginatorSubject$.next();
      this.destroyPaginatorSubject$.complete();
    }
  }

  applySorting(sorting: Sorting): void {
    this._sortingSubject$.next(sorting);
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this.pageAndSizeSubject$.next({ pageIndex, pageSize });
  }

  sortRequests(
    requests: RequestsOverviewModel[],
    clickedColumn: string,
    descending: boolean,
  ): RequestsOverviewModel[] {
    const sortedRequests = [...requests];
    sortedRequests.sort((a, b) => {
      const property = clickedColumn as keyof RequestsOverviewModel;
      const valueA = a[property];
      const valueB = b[property];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return descending ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return descending ? valueB - valueA : valueA - valueB;
      }
      if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        return descending ? (valueA ? 1 : -1) : valueA ? -1 : 1;
      }
      return 0;
    });
    return sortedRequests;
  }
}
